import {
  Component,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Renderer2,
  HostListener,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Themeable, ThemeColors } from '@optimistic-tanuki/theme-lib';
import type { Emphasis, Tone } from '../interfaces/variant.contract';

/** @deprecated Use `tone` + `emphasis` (+ decorative `surface`). */
export type ModalVariant = 'default' | 'glass' | 'gradient' | 'bordered';

/** Decorative surface chrome, folded out of the legacy variant. */
export type ModalSurface = 'flat' | 'glass' | 'gradient' | 'bordered';

/** Modal emphasis: the shared contract plus a `flat` (default) treatment. */
export type ModalEmphasis = Emphasis | 'flat';

const MODAL_VARIANT_BRIDGE: Record<
  ModalVariant,
  { emphasis: ModalEmphasis; surface: ModalSurface }
> = {
  default: { emphasis: 'flat', surface: 'flat' },
  glass: { emphasis: 'soft', surface: 'glass' },
  gradient: { emphasis: 'soft', surface: 'gradient' },
  bordered: { emphasis: 'outline', surface: 'bordered' },
};

/**
 * Modal size options
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * Modal position options
 */
export type ModalPosition =
  | 'center'
  | 'top'
  | 'sidebar-left'
  | 'sidebar-right'
  | 'bottom';

/**
 * Standardized Modal Component
 *
 * Fully accessible modal with focus trap, keyboard navigation, and theme support.
 *
 * @example
 * ```html
 * <otui-modal
 *   [visible]="showModal"
 *   [size]="'lg'"
 *   [position]="'center'"
 *   [closable]="true"
 *   [backdrop]="true"
 *   [ariaLabel]="'User Details'"
 *   (close)="showModal = false"
 * >
 *   <h2 modal-title>User Details</h2>
 *   <p>Modal content here...</p>
 * </otui-modal>
 * ```
 */
@Component({
  selector: 'otui-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  host: {
    '[class.theme]': 'theme',
    '[class.size-sm]': 'size === "sm"',
    '[class.size-md]': 'size === "md"',
    '[class.size-lg]': 'size === "lg"',
    '[class.size-xl]': 'size === "xl"',
    '[class.size-full]': 'size === "full"',
    '[class.position-center]': 'position === "center"',
    '[class.position-top]': 'position === "top"',
    '[class.position-sidebar-left]': 'position === "sidebar-left"',
    '[class.position-sidebar-right]': 'position === "sidebar-right"',
    '[class.position-bottom]': 'position === "bottom"',
    '[style.--local-background]': 'background',
    '[style.--local-foreground]': 'foreground',
    '[style.--local-accent]': 'accent',
    '[style.--local-complement]': 'complement',
    '[style.--local-border-color]': 'borderColor',
    '[style.--local-transition-duration]': 'transitionDuration',
  },
})
export class ModalComponent
  extends Themeable
  implements AfterViewInit, OnChanges, OnDestroy
{
  private renderer = inject(Renderer2);

  // ==================== NEW STANDARD INPUTS ====================

  /** Controls modal visibility */
  @Input() visible = false;

  /** Modal heading/title */
  @Input() heading = '';

  /** Modal size */
  @Input() size: ModalSize = 'md';

  /** Modal position */
  @Input() position: ModalPosition = 'center';

  /** Canonical semantic tone. */
  @Input() tone: Tone = 'neutral';
  /** Canonical visual treatment (`flat` = the default surface). */
  @Input() emphasis: ModalEmphasis = 'flat';
  /** Decorative surface chrome, folded out of the legacy `variant`. */
  @Input() surface: ModalSurface = 'flat';

  /**
   * Legacy visual variant. Folds onto the canonical `emphasis` axis plus the
   * decorative `surface` flag via {@link MODAL_VARIANT_BRIDGE}.
   *
   * @deprecated Use `tone` + `emphasis` (+ `surface`) instead.
   */
  @Input() set variant(value: ModalVariant) {
    this._variant = value;
    const binding = MODAL_VARIANT_BRIDGE[value];
    if (binding) {
      this.emphasis = binding.emphasis;
      this.surface = binding.surface;
    }
  }
  get variant(): ModalVariant {
    return this._variant;
  }
  private _variant: ModalVariant = 'default';

  /** Show close button */
  @Input() closable = true;

  /** Show backdrop overlay */
  @Input() backdrop = true;

  /** Close on backdrop click */
  @Input() closeOnBackdrop = true;

  /** Close on Escape key */
  @Input() closeOnEscape = true;

  /** Trap focus within modal */
  @Input() focusTrap = true;

  /** Lock body scroll when modal is open */
  @Input() lockScroll = true;

  /** ARIA label for the dialog */
  @Input() ariaLabel?: string;

  /** ARIA labelledby reference (ID of title element) */
  @Input() ariaLabelledBy?: string;

  /** ARIA describedby reference */
  @Input() ariaDescribedBy?: string;

  /** Custom z-index */
  @Input() zIndex?: number;

  // ==================== OUTPUTS ====================

  /** Emitted when modal is closed */
  @Output() close = new EventEmitter<void>();

  /** Emitted when modal is opened */
  @Output() open = new EventEmitter<void>();

  /** Emitted when backdrop is clicked */
  @Output() backdropClick = new EventEmitter<void>();

  // ==================== DEPRECATED INPUTS (Backward Compatibility) ====================

  /**
   * @deprecated Use 'position' input instead. Will be removed in v2.0.
   */
  @Input() set mode(
    value:
      | 'sidebar'
      | 'sidebar-left'
      | 'trough'
      | 'standard-modal'
      | 'captive-modal'
  ) {
    console.warn(
      `[otui-modal] Warning: "mode" input is deprecated. Use "position" and "size" inputs instead. Will be removed in v2.0.`
    );
    this._legacyMode = value;
    this._convertLegacyMode(value);
  }
  get mode() {
    return this._legacyMode;
  }
  _legacyMode:
    | 'sidebar'
    | 'sidebar-left'
    | 'trough'
    | 'standard-modal'
    | 'captive-modal' = 'standard-modal';

  /**
   * @deprecated Use 'close' output instead. Will be removed in v2.0.
   * This is kept for backward compatibility but will be removed.
   */
  @Output() closeModal = new EventEmitter<void>();

  // ==================== INTERNAL ====================

  @ViewChild('modalDialog', { static: false })
  modalDialog!: ElementRef<HTMLDivElement>;
  @ViewChild('closeButton', { static: false })
  closeButton!: ElementRef<HTMLButtonElement>;

  private focusableElements: HTMLElement[] = [];
  private previouslyFocusedElement: HTMLElement | null = null;
  private bodyScrollLock = false;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private focusTimer: ReturnType<typeof setTimeout> | null = null;

  // ==================== LIFECYCLE ====================

  ngAfterViewInit(): void {
    if (this.visible) {
      this.onModalOpen();
    }
  }

  /**
   * React to declarative visibility changes driven by the `[visible]` input.
   *
   * Many host components (e.g. the HAI about modal) toggle the modal purely
   * through the `visible` input binding rather than the imperative
   * `show()` / `hide()` methods. Without this hook the focus trap, scroll lock
   * and return-focus behaviour would never engage for those modals. We only act
   * on subsequent changes so the initial render is handled by ngAfterViewInit.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const visibleChange = changes['visible'];
    if (!visibleChange || visibleChange.firstChange) {
      return;
    }

    if (this.visible) {
      // Defer so the dialog is rendered (the template is gated on `visible`)
      // before we query focusable elements and move focus into the modal.
      this.openTimer = setTimeout(() => {
        this.openTimer = null;
        this.onModalOpen();
      }, 0);
    } else {
      this.onModalClose();
    }
  }

  override ngOnDestroy(): void {
    this.onModalClose();
  }

  // ==================== THEME ====================

  override applyTheme(colors: ThemeColors): void {
    this.background = colors.background;
    this.foreground = colors.foreground;
    this.accent = colors.accent;
    this.complement = colors.complementary;
    this.borderColor = colors.complementary;
    this.borderGradient = colors.complementaryGradients['light'];
    this.transitionDuration = '0.3s';
  }

  // ==================== VISIBILITY HANDLING ====================

  /**
   * Open the modal
   */
  show(): void {
    if (!this.visible) {
      this.visible = true;
      this.open.emit();
      this.openTimer = setTimeout(() => {
        this.openTimer = null;
        this.onModalOpen();
      }, 0);
    }
  }

  /**
   * Close the modal
   */
  hide(): void {
    if (this.visible) {
      this.visible = false;
      this.close.emit();
      this.onModalClose();
    }
  }

  // ==================== EVENT HANDLERS ====================

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (this.visible && this.closeOnEscape) {
      (event as KeyboardEvent).preventDefault();
      this.hide();
    }
  }

  onBackdropClick(event: MouseEvent): void {
    // Only close if clicking the backdrop itself, not the modal content
    if (event.target === event.currentTarget && this.closeOnBackdrop) {
      this.backdropClick.emit();
      this.hide();
    }
  }

  onCloseClick(): void {
    this.hide();
  }

  // ==================== FOCUS TRAP ====================

  private onModalOpen(): void {
    if (!this.visible) return;

    this.cancelFocusTimers();

    // Store previously focused element
    this.previouslyFocusedElement = document.activeElement as HTMLElement;

    // Lock body scroll
    if (this.lockScroll) {
      this.lockBodyScroll();
    }

    // Collect focusable elements
    if (this.focusTrap) {
      this.collectFocusableElements();
      // Focus first element or close button
      this.focusTimer = setTimeout(() => {
        this.focusTimer = null;
        if (!this.visible) return;
        if (this.closeButton?.nativeElement) {
          this.closeButton.nativeElement.focus();
        } else {
          this.focusFirstElement();
        }
      }, 100);
    }
  }

  private onModalClose(): void {
    this.cancelFocusTimers();
    const previouslyFocusedElement = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;

    // Restore focus
    if (
      previouslyFocusedElement?.isConnected &&
      !previouslyFocusedElement.hasAttribute('inert')
    ) {
      previouslyFocusedElement.focus();
    }

    // Unlock body scroll
    this.unlockBodyScroll();
  }

  private cancelFocusTimers(): void {
    if (this.openTimer !== null) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    if (this.focusTimer !== null) {
      clearTimeout(this.focusTimer);
      this.focusTimer = null;
    }
  }

  private collectFocusableElements(): void {
    if (!this.modalDialog?.nativeElement) return;

    const selector = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    this.focusableElements = Array.from(
      this.modalDialog.nativeElement.querySelectorAll(selector)
    ) as HTMLElement[];
  }

  @HostListener('keydown', ['$event'])
  onTabKey(event: Event): void {
    if (
      (event as KeyboardEvent).key !== 'Tab' ||
      !this.focusTrap ||
      !this.visible
    )
      return;

    // Tabs and async directory data can replace the projected controls.
    this.collectFocusableElements();
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement =
      this.focusableElements[this.focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement;
    const keyboardEvent = event as KeyboardEvent;

    if (keyboardEvent.shiftKey) {
      // Shift + Tab - move backward
      if (
        activeElement === firstElement ||
        !this.focusableElements.includes(activeElement)
      ) {
        keyboardEvent.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab - move forward
      if (activeElement === lastElement) {
        keyboardEvent.preventDefault();
        firstElement.focus();
      }
    }
  }

  private focusFirstElement(): void {
    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus();
    }
  }

  // ==================== BODY SCROLL LOCK ====================

  private lockBodyScroll(): void {
    if (this.bodyScrollLock) return;

    const scrollY = window.scrollY;
    this.renderer.setStyle(document.body, 'position', 'fixed');
    this.renderer.setStyle(document.body, 'top', `-${scrollY}px`);
    this.renderer.setStyle(document.body, 'left', '0');
    this.renderer.setStyle(document.body, 'right', '0');
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
    this.bodyScrollLock = true;
  }

  private unlockBodyScroll(): void {
    if (!this.bodyScrollLock) return;

    const scrollY = document.body.style.top;
    this.renderer.removeStyle(document.body, 'position');
    this.renderer.removeStyle(document.body, 'top');
    this.renderer.removeStyle(document.body, 'left');
    this.renderer.removeStyle(document.body, 'right');
    this.renderer.removeStyle(document.body, 'overflow');
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
    this.bodyScrollLock = false;
  }

  // ==================== LEGACY CONVERSION ====================

  private _convertLegacyMode(mode: string): void {
    switch (mode) {
      case 'sidebar':
      case 'sidebar-right':
        this.position = 'sidebar-right';
        break;
      case 'sidebar-left':
        this.position = 'sidebar-left';
        break;
      case 'trough':
        this.position = 'bottom';
        break;
      case 'captive-modal':
        this.position = 'center';
        this.size = 'full';
        break;
      case 'standard-modal':
      default:
        this.position = 'center';
        break;
    }
  }
}
